import { useMemo, useReducer } from "react";
import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { settings } from "../../../shim/jsbridge";
import { extractWarnings } from "../utils";
import { yaraQueryKey } from "../queryKeys";

// UTILS
const errorRegex = /(?<line>\d+:)/;

const extractEditorError = (errors = []) => {
  if (errors.length > 1) return null;
  return errors.map(error => {
    const { line = 1 } = error.match(errorRegex)?.groups || {};
    return { msg: error, line: parseInt(line, 10) };
  })[0];
};

const determineNeedsSave = ({
  enableChanged,
  enableTiCloudChanged,
  rulesetNameChanged,
  ruleSelectedChanged,
  editorValueChanged
}) =>
  enableChanged ||
  enableTiCloudChanged ||
  rulesetNameChanged ||
  ruleSelectedChanged ||
  editorValueChanged;

const clearChanges = () => ({
  enableChanged: false,
  enableTiCloudChanged: false,
  rulesetNameChanged: false,
  ruleSelectedChanged: false,
  editorValueChanged: false
});

// ACTIONS
const ActionEnum = Object.freeze({
  SAVE_ERROR: "save_error",
  RULE_SAVED: "rule_saved",
  RULESET_ENABLE: "ruleset_enable",
  CLOUD_ENABLE: "cloud_enable",
  RULESET_NAME_CHANGED: "ruleset_name_changed",
  RULESET_RENAME_MODE: "ruleset_rename_mode",
  RULE_SELECTED: "rule_selected",
  RULE_FETCHED: "rule_fetched",
  EDITOR_VALUE_CHANGED: "editor_value_changed"
});

// STATE
const yaraModalInitialState = {
  initEditorValue: "",
  initEnableRuleset: true,
  initRulesetName: "",
  enableRuleset: true,
  initEnableRulesetTiCloud: settings.YARA_ENABLE_AUTO_UPLOAD,
  initAutoCloudRetro: settings.YARA_AUTO_CLOUD_RETRO,
  enableRulesetTiCloud: settings.YARA_ENABLE_AUTO_UPLOAD,
  rulesetName: "",
  rulesetNameError: false,
  rulesetRenameMode: false,
  needsSave: false,
  changes: {
    enableChanged: false,
    enableTiCloudChanged: false,
    rulesetNameChanged: false,
    ruleSelectedChanged: false,
    editorValueChanged: false
  },
  isEditorEmpty: true,
  ruleId: null, // currently selected ruleId
  activeRuleId: null, // active ruleId => eg ruleId which is on top of history
  isPublishedToSdm: false,
  ruleSaved: false,
  warnings: [],
  editorError: null
};
const useYaraEditorReducer = ruleId => {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(
    (prevState, { type, payload }) => {
      let needsSaveConditions = true;
      switch (type) {
        case ActionEnum.SAVE_ERROR:
          return {
            ...prevState,
            ruleSaved: false,
            editorError: extractEditorError(payload)
          };
        case ActionEnum.RULE_SAVED:
          queryClient.invalidateQueries({
            queryKey: yaraQueryKey.fetchRule(payload.details.id)
          });
          return {
            ...prevState,
            ruleSaved: true,
            ruleId: payload.details.id,
            activeRuleId: payload.details.id, // on save, we load the new saved rule as currently active
            needsSave: false,
            changes: { ...clearChanges() }
          };
        case ActionEnum.RULESET_ENABLE:
          needsSaveConditions = {
            ...prevState.changes,
            enableChanged: prevState.initEnableRuleset !== payload
          };
          return {
            ...prevState,
            enableRuleset: payload,
            changes: needsSaveConditions,
            needsSave: determineNeedsSave(needsSaveConditions)
          };
        case ActionEnum.CLOUD_ENABLE:
          needsSaveConditions = {
            ...prevState.changes,
            enableTiCloudChanged: prevState.initEnableRulesetTiCloud !== payload
          };
          return {
            ...prevState,
            enableRulesetTiCloud: payload,
            changes: needsSaveConditions,
            needsSave: determineNeedsSave(needsSaveConditions)
          };
        case ActionEnum.RULESET_NAME_CHANGED:
          needsSaveConditions = {
            ...prevState.changes,
            rulesetNameChanged: prevState.initRulesetName !== payload.value
          };
          return {
            ...prevState,
            rulesetName: payload.value,
            rulesetNameError: payload.hasError,
            changes: needsSaveConditions,
            needsSave: determineNeedsSave(needsSaveConditions)
          };
        case ActionEnum.RULESET_RENAME_MODE:
          return {
            ...prevState,
            rulesetRenameMode: payload.value
          };
        case ActionEnum.RULE_SELECTED:
          needsSaveConditions = {
            ...prevState.changes,
            ruleSelectedChanged: prevState.activeRuleId !== payload
          };
          return {
            ...prevState,
            ruleId: payload,
            changes: needsSaveConditions,
            needsSave: determineNeedsSave(needsSaveConditions)
          };
        case ActionEnum.RULE_FETCHED:
          return {
            ...prevState,
            isEditorEmpty: false,
            initEditorValue: payload.details.content,
            initEnableRuleset: !payload.details.is_disabled,
            initEnableRulesetTiCloud: payload.details.saved_in_cloud,
            initRulesetName: payload.details.name,
            enableRuleset: !payload.details.is_disabled,
            enableRulesetTiCloud: payload.details.saved_in_cloud,
            editorError: extractEditorError(payload.errors),
            warnings: extractWarnings(payload?.warnings),
            rulesetName: payload.details.name,
            activeRuleId: !prevState.activeRuleId
              ? ruleId
              : prevState.activeRuleId, // setActiveRule on initial fetch only
            isPublishedToSdm: payload.details.is_published_to_sdm
          };
        case ActionEnum.EDITOR_VALUE_CHANGED:
          needsSaveConditions = {
            ...prevState.changes,
            editorValueChanged: prevState.initEditorValue !== payload
          };
          return {
            ...prevState,
            isEditorEmpty: payload.length < 1,
            changes: needsSaveConditions,
            needsSave: determineNeedsSave(needsSaveConditions)
          };
        default:
          throw new Error("Invalid action type");
      }
    },
    yaraModalInitialState,
    () => ({ ...yaraModalInitialState, ruleId })
  );

  const actions = useMemo(
    () => ({
      saveError: errors =>
        dispatch({
          type: ActionEnum.SAVE_ERROR,
          payload: errors
        }),
      ruleSaved: respData =>
        dispatch({
          type: ActionEnum.RULE_SAVED,
          payload: respData
        }),
      rulesetEnable: value =>
        dispatch({
          type: ActionEnum.RULESET_ENABLE,
          payload: value
        }),
      rulesetCloudEnable: value =>
        dispatch({
          type: ActionEnum.CLOUD_ENABLE,
          payload: value
        }),
      rulesetNameChanged: (value, hasError) =>
        dispatch({
          type: ActionEnum.RULESET_NAME_CHANGED,
          payload: { value, hasError }
        }),
      rulesetRenameMode: value =>
        dispatch({
          type: ActionEnum.RULESET_RENAME_MODE,
          payload: { value }
        }),
      ruleSelected: ruleID =>
        dispatch({
          type: ActionEnum.RULE_SELECTED,
          payload: ruleID
        }),
      ruleFetch: respData =>
        dispatch({
          type: ActionEnum.RULE_FETCHED,
          payload: respData
        }),
      editorValueChanged: value =>
        dispatch({
          type: ActionEnum.EDITOR_VALUE_CHANGED,
          payload: value
        })
    }),
    [dispatch]
  );
  return [state, actions];
};

useYaraEditorReducer.PropTypes = {
  id: PropTypes.number
};

export default useYaraEditorReducer;
